import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { BookOpen } from '@phosphor-icons/react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { devApi } from './devApi';

/** 繰り返している不具合をナレッジの下書きページとして残す（公開はしない）。 */
export const KnowledgeDraftDialog = ({ devId, initial, onClose }) => {
  const [f, setF] = useState({ title: '', problem: '', cause: '', countermeasure: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (initial) {
      setF({
        title: initial.title || '', problem: initial.problem || '',
        cause: initial.cause || '', countermeasure: initial.countermeasure || '',
      });
      setDone(null);
    }
  }, [initial]);

  const save = async () => {
    setBusy(true);
    try {
      const r = await devApi.knowledgeDraft({ development_id: devId, ...f });
      setDone(r);
      toast.success('ナレッジの下書きを作成しました');
    } catch (e) {
      toast.error(e.response?.data?.detail || '作成できませんでした');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!initial} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-white" data-testid="dev-knowledge-draft-dialog">
        <DialogHeader>
          <DialogTitle>この学びをナレッジ記事にする</DialogTitle>
          <DialogDescription>
            下書きとして保存します（公開はされません）。ナレッジ側で加筆してから公開してください。
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-2" data-testid="dev-knowledge-draft-done">
            <p className="text-sm text-zinc-800">下書きを作成しました。</p>
            <a href={done.url} className="text-sm font-semibold text-teal-800 underline"
              data-testid="dev-knowledge-draft-link">{done.title} を開く</a>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold text-zinc-700">タイトル</Label>
              <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
                data-testid="dev-knowledge-draft-title" />
            </div>
            {[['problem', '現象・問題'], ['cause', '原因'], ['countermeasure', '対策・標準化すること']]
              .map(([k, label]) => (
                <div key={k}>
                  <Label className="text-xs font-semibold text-zinc-700">{label}</Label>
                  <Textarea rows={3} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })}
                    data-testid={`dev-knowledge-draft-${k}`} />
                </div>
              ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dev-knowledge-draft-close">閉じる</Button>
          {!done && (
            <Button onClick={save} disabled={busy || !f.title.trim()}
              data-testid="dev-knowledge-draft-save">
              <BookOpen size={14} className="mr-1" /> {busy ? '作成中…' : '下書きを作成'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
